import { Check, Copy, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { generateIntegers, uuidV4 } from "../lib/random";

type Mode = "numbers" | "uuid";

/** Uniform random index in [0, max) via CSPRNG with rejection sampling. */
function secureBelow(max: number): number {
  if (max <= 1) return 0;
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  const buf = new Uint32Array(1);
  let x = 0;
  do {
    crypto.getRandomValues(buf);
    x = buf[0];
  } while (x >= limit);
  return x % max;
}

function clampInt(value: string, lo: number, hi: number, fallback: number) {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
}

function tabClass(active: boolean) {
  return `rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
    active
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:text-foreground"
  }`;
}

export function RandomGenerator() {
  const uid = useId();
  const [mode, setMode] = useState<Mode>("numbers");
  const [min, setMin] = useState("1");
  const [max, setMax] = useState("100");
  const [count, setCount] = useState("5");
  const [allowDuplicates, setAllowDuplicates] = useState(true);
  const [uuidCount, setUuidCount] = useState("5");
  const [lines, setLines] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const regenerate = useCallback(() => {
    setCopied(false);
    if (mode === "numbers") {
      const lo = Math.trunc(Number(min));
      const hi = Math.trunc(Number(max));
      const c = clampInt(count, 1, 1000, 1);
      const out = generateIntegers(lo, hi, c, allowDuplicates, secureBelow);
      setLines(out.map(String));
    } else {
      const c = clampInt(uuidCount, 1, 100, 1);
      setLines(
        Array.from({ length: c }, () =>
          uuidV4(crypto.getRandomValues(new Uint8Array(16))),
        ),
      );
    }
  }, [mode, min, max, count, allowDuplicates, uuidCount]);

  // Generate on mount and whenever inputs change (client-only → no hydration
  // mismatch, since the SSR markup has an empty output).
  useEffect(() => {
    regenerate();
  }, [regenerate]);

  const text = lines.join(mode === "numbers" ? ", " : "\n");

  const copy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
      <div className="inline-flex gap-1 rounded-lg border border-border p-1">
        <button
          type="button"
          aria-pressed={mode === "numbers"}
          onClick={() => setMode("numbers")}
          className={tabClass(mode === "numbers")}
        >
          {m.rg_mode_numbers()}
        </button>
        <button
          type="button"
          aria-pressed={mode === "uuid"}
          onClick={() => setMode("uuid")}
          className={tabClass(mode === "uuid")}
        >
          {m.rg_mode_uuid()}
        </button>
      </div>

      {mode === "numbers" ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-min`}>{m.rg_min()}</Label>
            <Input
              id={`${uid}-min`}
              type="number"
              value={min}
              onChange={(e) => setMin(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-max`}>{m.rg_max()}</Label>
            <Input
              id={`${uid}-max`}
              type="number"
              value={max}
              onChange={(e) => setMax(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-count`}>{m.rg_count()}</Label>
            <Input
              id={`${uid}-count`}
              type="number"
              min={1}
              max={1000}
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2.5 text-sm sm:col-span-3">
            <input
              type="checkbox"
              checked={allowDuplicates}
              onChange={(e) => setAllowDuplicates(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            {m.rg_allow_duplicates()}
          </label>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-uuids`}>{m.rg_uuid_count()}</Label>
            <Input
              id={`${uid}-uuids`}
              type="number"
              min={1}
              max={100}
              value={uuidCount}
              onChange={(e) => setUuidCount(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button type="button" onClick={regenerate} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          {m.rg_generate()}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={copy}
          className="gap-2"
          disabled={!text}
        >
          {copied ? (
            <Check className="h-4 w-4 text-emerald-500" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
          {copied ? m.rg_copied() : m.rg_copy()}
        </Button>
      </div>

      <textarea
        readOnly
        data-testid="random-output"
        aria-label={m.rg_output_label()}
        value={text}
        placeholder={m.rg_empty()}
        className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
      />
    </div>
  );
}
